/**
 * Spieleransicht (TV/Projektion, remote im Browser): ein geheimer Link pro Kampagne,
 * der genau die Karte zeigt, die der DM freigibt. Alles wird serverseitig gefiltert.
 */
import { z } from 'zod';
import { isRevealed, pinLabelFromTitle, type FogState, type MapDTO, type MapGrid, type MapTemplate, type MapTokenDTO } from './map.js';

export interface ViewRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlayerPinDTO {
  id: string;
  x: number;
  y: number;
  /** Eigene Beschriftung oder Raumnummer – nie der (evtl. verräterische) Notiztitel. */
  label: string;
  color: string | null;
}

export interface PlayerMapDTO {
  noteId: string;
  grid: MapGrid;
  fog: FogState;
  image: { width: number; height: number } | null;
  pins: PlayerPinDTO[];
  tokens: MapTokenDTO[];
  templates: MapTemplate[];
  /** Vom DM vorgegebener Ausschnitt (Bildkoordinaten). */
  view: ViewRect | null;
}

export interface ScreenDTO {
  campaignName: string;
  map: PlayerMapDTO | null;
}

/** Einstellungen für den DM. */
export interface ScreenSettingsDTO {
  enabled: boolean;
  token: string | null;
  mapNoteId: string | null;
  mapTitle: string | null;
  view: ViewRect | null;
}

const num = z.number().finite();
export const viewRectSchema = z.object({ x: num, y: num, w: num.positive(), h: num.positive() });
export const updateScreenSchema = z.object({
  mapNoteId: z.string().uuid().nullable().optional(),
  view: viewRectSchema.nullable().optional(),
});

/** Raumnummer aus dem Notiztitel, sonst leer (Titel bleiben geheim). */
function safeLabel(title: string | null): string {
  if (!title) return '';
  const label = pinLabelFromTitle(title);
  return label === title ? '' : label;
}

/**
 * Filtert eine Karte für Spieler: nur freigegebene Pins außerhalb des Nebels,
 * keine verborgenen Tokens, Tokens im Nebel nur für Spielercharaktere,
 * keine TP von Gegnern und keine Verknüpfungen auf (DM-)Notizen.
 */
export function filterMapForPlayers(map: MapDTO, view: ViewRect | null): PlayerMapDTO {
  const fog = map.fog;
  return {
    noteId: map.noteId,
    grid: map.grid,
    fog,
    image: map.image,
    view,
    templates: map.templates,
    pins: map.pins
      .filter((p) => p.visibility === 'all' && isRevealed(fog, p.x, p.y))
      .map((p) => ({ id: p.id, x: p.x, y: p.y, label: p.label || safeLabel(p.noteTitle), color: p.color })),
    tokens: map.tokens
      .filter((t) => !t.hidden && (t.combat?.kind === 'pc' || isRevealed(fog, t.x, t.y)))
      .map((t) => ({
        ...t,
        noteId: null,
        combatantId: null,
        combat: t.combat
          ? t.combat.kind === 'pc'
            ? t.combat
            : { ...t.combat, hpCurrent: null, hpMax: null, tempHp: 0 }
          : null,
      })),
  };
}

/**
 * Karte für angemeldete Spieler in der App: dieselben Regeln wie die Spieleransicht,
 * aber im Format der DM-Karte. Verknüpfungen bleiben nur zu Notizen, die der Spieler
 * ohnehin sehen darf; Token-Bilder laufen über eine eigene, geprüfte Adresse.
 */
export function filterMapForMember(map: MapDTO, visibleNoteIds: ReadonlySet<string>, tokenImageUrl: (t: MapTokenDTO) => string): MapDTO {
  const fog = map.fog;
  const visible = (id: string | null) => (id && visibleNoteIds.has(id) ? id : null);
  return {
    ...map,
    pins: map.pins
      .filter((p) => p.visibility === 'all' && isRevealed(fog, p.x, p.y))
      .map((p) => {
        const noteId = visible(p.noteId);
        return {
          ...p,
          label: p.label || (noteId ? pinLabelFromTitle(p.noteTitle ?? '') : safeLabel(p.noteTitle)),
          noteId,
          noteTitle: noteId ? p.noteTitle : null,
          noteTypeKey: noteId ? p.noteTypeKey : null,
        };
      }),
    tokens: map.tokens
      .filter((t) => !t.hidden && (t.combat?.kind === 'pc' || isRevealed(fog, t.x, t.y)))
      .map((t) => ({
        ...t,
        noteId: visible(t.noteId),
        combatantId: null,
        image: null,
        imageUrl: t.imageUrl ? tokenImageUrl(t) : null,
        combat: t.combat
          ? t.combat.kind === 'pc'
            ? t.combat
            : { ...t.combat, hpCurrent: null, hpMax: null, tempHp: 0 }
          : null,
      })),
  };
}
